import React from 'react';
import Text from '@/components/ui/Text';
import { canViewCorrectAnswers, noAnswerProvided, renderCorrectnessIcon } from '@/components/staffer/Grading/utils';
import { StudentAnswerProps } from '../types';

export const multipleChoiceQuestionAnswerAttributes = ['is_correct', 'selected_choices_text', 'gather_attempts'];

const ATTEMPT_INDEXES = [0, 1];

const MultiAttemptAnswerTable = ({ question, questionAnswer }: StudentAnswerProps) => {
  // AIDEV-NOTE: a gather_attempts entry is the student's whole answer for that attempt, so a
  // multi-correct selection arrives comma-joined ("0,2") — never a bare choice index (PLAT-18061).
  const attempts = ATTEMPT_INDEXES.map(i => (questionAnswer.gather_attempts?.[i] || '').split(','));

  return (
    <table className="multi-correct-answer-preview answer-preview-table">
      <thead>
        <tr>
          <td>Answer Choices</td>
          <td>Selected Answer (1st)</td>
          <td>Selected Answer (2nd)</td>
          <td>Correct Selections</td>
        </tr>
      </thead>
      <tbody>
        {question.answer_choices.filter(Boolean).map((answerChoice, i) => {
          const index = i.toString();
          const answerChoiceIsCorrect = question.correct_choices.includes(index);

          return (
            <tr key={`${question.id}-choice-${i}`}>
              <td>{answerChoice}</td>
              {attempts.map((selections, attemptIndex) => (
                <td key={`${question.id}-choice-${i}-attempt-${attemptIndex}`}>
                  {selections.includes(index) && renderCorrectnessIcon(answerChoiceIsCorrect)}
                </td>
              ))}
              <td>
                {answerChoiceIsCorrect && <i aria-hidden className="fa fa-check" />}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const MultipleChoiceQuestionStudentAnswer = ({ question, questionAnswer }: StudentAnswerProps) => {
  const showCorrectAnswers = canViewCorrectAnswers();

  if (!questionAnswer) return noAnswerProvided();

  if ((questionAnswer.gather_attempts?.length ?? 0) > 0) {
    return <MultiAttemptAnswerTable question={question} questionAnswer={questionAnswer} />;
  }

  return (
    <table className="multiple-choice-answer-preview answer-preview-table tw-min-w-[29em]">
      <thead>
        <tr>
          <Text asChild size="sm" weight="bold">
            <th className="tw-p-2">Answer Choice</th>
          </Text>
          <Text asChild size="sm" weight="bold">
            <th>Selected Answer</th>
          </Text>
          {showCorrectAnswers && (
            <Text asChild size="sm" weight="bold">
              <th>Correct Selection</th>
            </Text>
          )}
        </tr>
      </thead>
      <tbody>
        {question.answer_choices.filter(Boolean).map((answerChoice, i) => {
          const index = i.toString();
          const answerChoiceIsCorrect = question.correct_choices.includes(index);
          const answers = (questionAnswer.answer || '').split(',');
          const isCorrect = answerChoiceIsCorrect && answers.includes(index);

          return (
            <tr key={answerChoice} className="tw-text-center">
              <td className="tw-align-middle">
                <Text size="sm" weight="bold">
                  {answerChoice}
                </Text>
              </td>
              <td>
                {answers.includes(index) && renderCorrectnessIcon(isCorrect, '!tw-text-2xl')}
              </td>
              {showCorrectAnswers && (
                <td>
                  {answerChoiceIsCorrect && (
                    <i aria-hidden className="fa fa-check-circle !tw-text-2xl" />
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MultipleChoiceQuestionStudentAnswer;
